import React, { useState } from 'react';
import { Form, Button, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FaPlus, FaTrash, FaCog } from 'react-icons/fa';
import './ModifierOptionsSection.scss';

interface ModifierOption {
  id: string;
  name: string;
  price_delivery: number;
  price_online: number;
  price_desk: number;
}

interface ModifierOptionsSectionProps {
  options: ModifierOption[];
  setOptions: React.Dispatch<React.SetStateAction<ModifierOption[]>>;
  modifierName: string;
}

const ModifierOptionsSection: React.FC<ModifierOptionsSectionProps> = ({
  options,
  setOptions,
  modifierName
}) => {
  const [newOptionName, setNewOptionName] = useState('');
  const [showAddOptionForm, setShowAddOptionForm] = useState(false);

  const handleAddOption = () => {
    if (!newOptionName.trim()) {
      toast.error('El nombre de la opción es requerido');
      return;
    }

    const newOption: ModifierOption = {
      id: `mod_option_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: newOptionName.trim(),
      price_delivery: 0,
      price_online: 0,
      price_desk: 0
    };

    setOptions([...options, newOption]);
    setNewOptionName('');
    setShowAddOptionForm(false);
    toast.success('Opción agregada exitosamente');
  };

  const handleUpdateOption = (optionId: string, field: string, value: number | string) => {
    setOptions(options.map(option => 
      option.id === optionId ? { ...option, [field]: value } : option
    ));
  };

  const handleDeleteOption = (optionId: string) => {
    setOptions(options.filter(option => option.id !== optionId));
    toast.success('Opción eliminada');
  };

  return (
    <div className="modifier-options-section">
      <div className="section-header">
        <h5 className="section-title">
          <FaCog className="section-icon" />
          Opciones de {modifierName || 'Modificador'} ({options.length})
        </h5>
        
        {!showAddOptionForm && (
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => setShowAddOptionForm(true)}
            className="add-option-btn"
          >
            <FaPlus /> Agregar
          </Button>
        )}
      </div>

      {/* Add Option Form */}
      {showAddOptionForm && (
        <div className="add-option-form">
          <Row className="align-items-end">
            <Col md={8}>
              <Form.Group>
                <Form.Label>Nombre de la Opción</Form.Label>
                <Form.Control
                  type="text"
                  value={newOptionName}
                  onChange={(e) => setNewOptionName(e.target.value)}
                  placeholder="Ej: Término medio, Extra queso, Caliente"
                  className="form-control-custom"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddOption()}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <div className="button-group">
                <Button
                  variant="success"
                  size="sm"
                  onClick={handleAddOption}
                  className="save-btn"
                >
                  Agregar
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setShowAddOptionForm(false);
                    setNewOptionName('');
                  }}
                  className="cancel-btn"
                >
                  Cancelar
                </Button>
              </div>
            </Col>
          </Row>
        </div>
      )}

      {/* Options Table */}
      {options.length > 0 ? (
        <div className="options-table-container">
          <div className="table-responsive">
            <table className="options-table">
              <thead>
                <tr>
                  <th>Nombre de la Opción</th>
                  <th>Delivery</th>
                  <th>Online</th>
                  <th>Mesa</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {options.map((option) => (
                  <tr key={option.id} className="option-row">
                    <td className="name-cell">
                      <Form.Control
                        type="text"
                        value={option.name}
                        onChange={(e) => handleUpdateOption(option.id, 'name', e.target.value)}
                        className="option-name-input"
                        placeholder="Nombre de la opción"
                      />
                    </td>
                    <td className="price-cell">
                      <Form.Control
                        type="number"
                        min="0"
                        step="0.01"
                        value={option.price_delivery}
                        onChange={(e) => handleUpdateOption(option.id, 'price_delivery', Number(e.target.value))}
                        className="price-input"
                      />
                    </td>
                    <td className="price-cell">
                      <Form.Control
                        type="number"
                        min="0"
                        step="0.01"
                        value={option.price_online}
                        onChange={(e) => handleUpdateOption(option.id, 'price_online', Number(e.target.value))}
                        className="price-input"
                      />
                    </td>
                    <td className="price-cell">
                      <Form.Control
                        type="number"
                        min="0"
                        step="0.01"
                        value={option.price_desk}
                        onChange={(e) => handleUpdateOption(option.id, 'price_desk', Number(e.target.value))}
                        className="price-input"
                      />
                    </td>
                    <td className="actions-cell">
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDeleteOption(option.id)}
                        className="delete-btn"
                      >
                        <FaTrash />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="empty-options">
          <p className="empty-message">
            No hay opciones agregadas. Haz clic en "Agregar" para crear la primera opción.
          </p>
        </div>
      )}
    </div>
  );
};

export default ModifierOptionsSection;
